/**
 * Protocol between the practice page and the API's realtime voice bridge.
 * Binary frames carry PCM16 mono 24 kHz audio in both directions; text
 * frames carry these JSON events.
 */
export type RealtimeAgentState = 'listening' | 'thinking' | 'speaking';

/** Played out to the end, cut off, or followed by the next answer's audio. */
export type PlaybackEnd = 'drained' | 'cleared' | 'next';

export type RealtimeClientEvent =
  | { type: 'played'; item_id: string; ms: number }
  /**
   * Once per answer: where the speaker ran out of audio mid-answer. Offsets
   * count from the answer's first sound; `end` says how its playback ended.
   */
  | {
      type: 'playback_gaps';
      item_id: string;
      played_ms: number;
      gaps: number;
      gap_ms: number;
      longest_gap_ms: number;
      offsets_ms: number[];
      end: PlaybackEnd;
    }
  /** On the live socket: the warm socket is ready, release at the next turn boundary. */
  | { type: 'handover' }
  /**
   * On the warm socket: become the authoritative session now. Every turn from
   * `cut_bytes` of this socket's audio on is this session's; without it, only
   * what it hears from now on.
   */
  | { type: 'promote'; cut_bytes?: number }
  /**
   * The user ends the call. The bridge closes the socket once every turn it
   * heard is written down or marked as on its way; then the debrief may be asked for.
   */
  | { type: 'hang_up' };

export type RealtimeServerEvent =
  | { type: 'ready' }
  /** `item_id` names the answer whose audio starts, on `speaking` only. */
  | { type: 'agent_state'; state: RealtimeAgentState; item_id?: string }
  | { type: 'interrupt' }
  | { type: 'transcript'; role: 'user' | 'assistant'; text: string }
  /**
   * The user stopped talking. `since_bytes` counts the audio bytes of this socket
   * up to just before the turn's end; what follows is not in any turn yet. This
   * task writes the turn, which ends at `end_bytes`.
   */
  | { type: 'turn_end'; since_bytes: number; end_bytes?: number }
  /** The character's words as they are generated, ahead of the voice. */
  | { type: 'transcript_delta'; item_id: string; delta: string }
  | { type: 'suggest_end' }
  /**
   * The persona said goodbye and hung up; every byte of its last line is sent.
   * Play it out, then hang up as if the user ended the call.
   */
  | { type: 'ended' }
  /** The voice failed on this answer; the call goes on. */
  | { type: 'tts_error' }
  /** A newer task is up: open a second socket with `?phase=warm` and tee the mic. */
  | { type: 'prepare_reconnect' }
  /**
   * Audio before `since_bytes` of this socket holds no turn still to be written:
   * drop it from what a newer task is replayed. Sent while that task is sought.
   */
  | { type: 'settled'; since_bytes: number }
  /**
   * The old task finished its last answer and wrote down, or marked as on its
   * way, every turn that ended before the handover. They end at `cut_bytes`.
   */
  | { type: 'released'; cut_bytes?: number }
  | { type: 'reconnect' }
  | { type: 'error'; message: string };

export const REALTIME_VOICE_PATH = '/voice/realtime';
export const REALTIME_VOICE_WARM_QUERY = 'phase=warm';
/** Close code: this task knows a newer one exists, retry the warm socket elsewhere. */
export const REALTIME_VOICE_NOT_NEWEST_CLOSE_CODE = 4410;
